import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useReducer,
} from 'react';
import { useRouter } from 'next/router';
import differenceInDays from 'date-fns/differenceInDays';
import _has from 'lodash/has';
import {
  ActionsEnum,
  ActionType,
  ContextValues,
  FilterParamsType,
  MinMaxType,
} from '@utils/types/searchListingsContext';
import { DatesRange, FuzzyDatesOffset } from '@utils/types/calendar';
import { useSearchListingsAPI } from '@utils/hooks/api/useSearchListingsAPI';
import {
  SegmentEventNamesEnum,
  SegmentEventPagesEnum,
  useAnalytics,
} from '@utils/hooks/useAnalytics';
import { Global } from '@utils/constants/global';

const defaultFilterParams = {
  moreFilters: {},
  fuzzyDatesOffset: {
    from: Global.FUZZY_DATE_OFFSET_DEFAULT,
    to: Global.FUZZY_DATE_OFFSET_DEFAULT,
  },
  mapBounds: '',
};

function init(initialState = defaultFilterParams) {
  return initialState;
}

function reducer(
  state: FilterParamsType,
  action: ActionType
): FilterParamsType {
  switch (action.type) {
    case ActionsEnum.RESET:
      return init();
    case ActionsEnum.DATES:
      return {
        ...state,
        dates: action.payload,
      };
    case ActionsEnum.FUZZY_DATES_OFFSET:
      return {
        ...state,
        fuzzyDatesOffset: action.payload,
      };
    case ActionsEnum.PRICE:
      return {
        ...state,
        price: action.payload,
      };
    case ActionsEnum.MARKET:
      return {
        ...state,
        market: action.payload,
        neighborhoods: [],
        mapBounds: '',
      };
    case ActionsEnum.MAP_BOUNDS:
      return {
        ...state,
        mapBounds: action.payload,
        market: null,
      };
    case ActionsEnum.NEIGHBORHOODS:
      return {
        ...state,
        neighborhoods: action.payload,
      };
    case ActionsEnum.BEDROOMS:
      return {
        ...state,
        bedrooms: action.payload,
      };
    case ActionsEnum.PET_FRIENDLY:
      return {
        ...state,
        petFriendly: action.payload,
      };
    case ActionsEnum.BULK_FILTERS:
      return {
        ...state,
        ...action.payload,
      };
    case ActionsEnum.SEARCH:
      return {
        ...state,
        search: action.payload,
      };
    default:
      throw new Error('Action not recognized');
  }
}

const SearchListingsContext = createContext<ContextValues>({
  listings: [],
  loading: false,
  error: null,
  filterParams: defaultFilterParams,
  changeDates: () => {},
  changeFuzzyDatesOffset: () => {},
  changePrice: () => {},
  changeMarket: () => {},
  resetFilters: () => {},
  changeMapBounds: () => {},
  changeNeighborhoods: () => {},
  changeBedrooms: () => {},
  changePetFriendly: () => {},
  changeBulkFilters: () => {},
  changeSearch: () => {},
});
SearchListingsContext.displayName = 'SearchListingsContext';

export function SearchListingsProvider(props: PropsWithChildren<{}>) {
  const { children } = props;

  const [filterParams, dispatch] = useReducer(
    reducer,
    defaultFilterParams,
    init
  );

  const { trackEvent } = useAnalytics();
  const router = useRouter();

  const { data, error, isLoading, isRefetching } =
    useSearchListingsAPI(filterParams);

  const resetFilters = () => dispatch({ type: ActionsEnum.RESET });

  const changeDates = (dates: DatesRange) => {
    dispatch({ type: ActionsEnum.DATES, payload: dates });
    if (dates.from && dates.to) {
      router.query.dates = [dates.from.toISOString(), dates.to.toISOString()];

      if (filterParams.fuzzyDatesOffset) {
        router.query.offsetFrom = filterParams.fuzzyDatesOffset.from.toString();
        router.query.offsetTo = filterParams.fuzzyDatesOffset.to.toString();
      }

      router.push(router, undefined, { scroll: false, shallow: false });
    } else {
      delete router.query.dates;
      router.push(router, undefined, { scroll: false, shallow: false });
    }

    trackEvent(SegmentEventNamesEnum.FILTER_APPLIED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterType: 'Dates',
      FilterValue: dates,
    });
  };

  const changeFuzzyDatesOffset = useCallback(
    (fuzzyDatesOffset: FuzzyDatesOffset) => {
      dispatch({
        type: ActionsEnum.FUZZY_DATES_OFFSET,
        payload: fuzzyDatesOffset,
      });

      trackEvent(SegmentEventNamesEnum.FUZZY_DATES_OFFSET_CHANGED, {
        PageName: SegmentEventPagesEnum.SEARCH_PAGE,
        FilterValue: fuzzyDatesOffset,
      });
    },
    []
  );

  const changePrice = (price: MinMaxType) => {
    dispatch({ type: ActionsEnum.PRICE, payload: price });

    trackEvent(SegmentEventNamesEnum.FILTER_APPLIED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterType: 'Price',
      FilterValue: price,
    });
  };

  const changeMarket = (market: string) => {
    dispatch({ type: ActionsEnum.MARKET, payload: market });

    router.query.market = market;
    router.push(router, undefined, { scroll: false, shallow: false });

    /**
     * If the market is NYC and the date range is less than 30 days,
     * reset the dates to null.
     */
    if (
      filterParams.dates?.from &&
      filterParams.dates.to &&
      market === 'new-york-city' &&
      differenceInDays(filterParams.dates.from, filterParams.dates.to) < 30
    ) {
      changeDates({
        from: null,
        to: null,
      });
    }

    trackEvent(SegmentEventNamesEnum.MARKET_CHANGED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterValue: market,
    });
  };

  const changeMapBounds = (bounds: string) => {
    dispatch({ type: ActionsEnum.MAP_BOUNDS, payload: bounds });
  };

  const changeNeighborhoods = (neighborhoods: string[]) => {
    dispatch({ type: ActionsEnum.NEIGHBORHOODS, payload: neighborhoods });

    trackEvent(SegmentEventNamesEnum.NEIGHBORHOODS_CHANGED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterValue: neighborhoods,
    });
  };

  const changeBedrooms = (bedrooms: MinMaxType) => {
    dispatch({ type: ActionsEnum.BEDROOMS, payload: bedrooms });

    trackEvent(SegmentEventNamesEnum.FILTER_APPLIED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterType: 'Bedrooms',
      FilterValue: bedrooms,
    });
  };

  const changePetFriendly = (petFriendly: boolean) => {
    dispatch({ type: ActionsEnum.PET_FRIENDLY, payload: petFriendly });

    router.query.petFriendly = petFriendly.toString();
    router.push(router, undefined, { scroll: false, shallow: false });

    trackEvent(SegmentEventNamesEnum.FILTER_APPLIED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterType: 'Pet Friendly',
      FilterValue: petFriendly,
    });
  };

  const changeBulkFilters = (filters: FilterParamsType) => {
    dispatch({
      type: ActionsEnum.BULK_FILTERS,
      payload: filters,
    });

    if (_has(filters, 'petFriendly')) {
      router.query.petFriendly = filters.petFriendly?.toString();
      router.push(router, undefined, { scroll: false, shallow: false });
    }

    Object.entries(filters).map((entry) => {
      trackEvent(SegmentEventNamesEnum.FILTER_APPLIED, {
        PageName: SegmentEventPagesEnum.SEARCH_PAGE,
        FilterType: entry[0],
        FilterValue: entry[1],
      });
    });
  };

  const changeSearch = (search: string) => {
    dispatch({ type: ActionsEnum.SEARCH, payload: search });
    router.query.search = encodeURIComponent(search);
    router.push(router, undefined, { scroll: false, shallow: false });

    trackEvent(SegmentEventNamesEnum.FILTER_APPLIED, {
      PageName: SegmentEventPagesEnum.SEARCH_PAGE,
      FilterType: 'Search',
      FilterValue: search,
    });
  };

  return (
    <SearchListingsContext.Provider
      value={{
        listings: data?.listings || [],
        error,
        loading: isLoading || isRefetching,
        filterParams,
        resetFilters,
        changeDates,
        changeFuzzyDatesOffset,
        changePrice,
        changeBedrooms,
        changeMarket,
        changeMapBounds,
        changeNeighborhoods,
        changePetFriendly,
        changeBulkFilters,
        changeSearch,
      }}
    >
      {children}
    </SearchListingsContext.Provider>
  );
}

export function useSearchListings() {
  const context = useContext(SearchListingsContext);
  if (context === undefined) {
    throw new Error(
      'useSearchListings must be used within an SearchListingsContext'
    );
  }
  return context;
}
